import Badge from '@atlaskit/badge';
import ArrowsDiagonalUpRightDownLeftIcon from '@atlaskit/icon-lab/core/arrows-diagonal-up-right-down-left';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import ChangesIcon from '@atlaskit/icon/core/changes';
import CopyIcon from '@atlaskit/icon/core/copy';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import DeleteIcon from '@atlaskit/icon/core/delete';
import EditIcon from '@atlaskit/icon/core/edit';
import FilterIcon from '@atlaskit/icon/core/filter';
import PinIcon from '@atlaskit/icon/core/pin';
import SortAscendingIcon from '@atlaskit/icon/core/sort-ascending';
import SortDescendingIcon from '@atlaskit/icon/core/sort-descending';
import TableCellClearIcon from '@atlaskit/icon/core/table-cell-clear';
import { type TriggerProps } from '@atlaskit/popup';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { actionBarPortalState } from '@databases/action-bar/selections-effect';
import {
  getDatabaseContainerType,
  getSelectionsAnalyticsAttributes,
  SendDatabaseSortEventSources,
  sendSortEvent,
  useAnalytics
} from '@databases/analytics';
import {
  contextualMenuState,
  type MenuData
} from '@databases/contextual-menu/contextual-menu-state';
import ContextualMenu, { type MenuItem } from '@databases/contextual-menu/ContextualMenu';
import { isDrilldownMenu } from '@databases/contextual-menu/DrilldownMenu';
import { MENU_WIDTH } from '@databases/contextual-menu/menu-constants';
import { useContextualMenu } from '@databases/contextual-menu/useContextualMenu';
import {
  hasAiSummaryHeaderRefresh,
  useAiSummaryRerunMenuItems,
  useIsAiSummaryFieldEnabled
} from '@databases/database-ai-fields';
import {
  InlineRovoPromptTriggerSource,
  openInlineRovoPrompt
} from '@databases/database-ai-first-creation-state/inline-rovo-prompt-state';
import { AskRovoIcon } from '@databases/database-ai-first-creation-ui/icons/ask-rovo-icon';
import { useIsDatabaseRovoAvailable } from '@databases/database-ai-first-creation-ui/rovo-enablement';
import { useDialog } from '@databases/dialog';
import FieldSettingsMenuContent from '@databases/field-settings-menus/FieldSettingsMenuContent';
import { useChangeFieldType } from '@databases/field-type/FieldTypeEdit';
import { useFieldTypeEntries } from '@databases/field-type/use-field-type-entries';
import { getFieldTypes } from '@databases/fields';
import {
  DatabaseTraceAction,
  DatabaseTraceSource,
  traceDatabaseAction
} from '@databases/frontend-metrics/perf-tracing';
import {
  ActiveHorizontalDragHandlerIcon,
  HideIcon,
  HorizontalDragHandlerIcon,
  InsertColumnAfterIcon,
  InsertColumnBeforeIcon,
  SortIcon
} from '@databases/icons';
import { FieldTypeIcon } from '@databases/icons/FieldTypeIcon';
import { messages } from '@databases/intl/messages';
import { SHORTCUTS } from '@databases/keyboard-shortcuts';
import usePanel from '@databases/panel/usePanel';
import usePanelState from '@databases/panel/usePanelState';
import { useQuickCalcMenuItem } from '@databases/quick-calc/useQuickCalcMenuItem';
import { type HoverProps, useComponent, useKeyboardShortcut } from '@databases/react';
import { getTextDelta } from '@databases/react/useTextDelta';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import {
  doClearEntireFields,
  doDeleteFieldsWithIds,
  doDuplicateEntireFields,
  doInsertFields,
  doMoveFields
} from '@databases/redux/contextual-menu/actions';
import { getService } from '@databases/service-container';
import { IGNORED_TABLE_ELEMENT_CLASS } from '@databases/table-resizer';
import { DatabaseFieldType } from '@databases/types/database';
import {
  type DatabaseFilter,
  type DatabaseFilterUsageType,
  type DatabaseViewSettings
} from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import { fieldTypeSupportsAskRovo } from '@databases/utils/database/field-types';
import {
  fieldHasCalculations,
  getDatabaseFieldRequired,
  getDatabaseFields,
  getExistingFieldNames
} from '@databases/utils/database/utils';
import {
  type ViewChangeType,
  ViewChangeTypes
} from '@databases/utils/database/view/view';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { afterNextLayout } from '@databases/utils/lib/raf';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { ViewPanelSettingType } from '@databases/views/types';
import useView from '@databases/views/useView';
import { useViewUiState } from '@databases/views/useViewUiState';
import React, {
  type ForwardedRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';
import { useSnapshot } from 'valtio';

import { getExperimentValue } from '@canvas/client-feature-flags';
import { manuallyFireExposureEvent } from '@canvas/client-feature-flags/experiments';
import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

import {
  createFieldSortSubmenuItems,
  getSelectedSortDirection
} from './field-sort-submenu.tsx';
import FieldMenuHeader, { type FieldMenuHeaderHandle } from './FieldMenuHeader.tsx';
import {
  FieldMenuSpotlightTarget,
  FieldMenuSpotlightTourProvider
} from './FieldMenuSpotlightTour.tsx';
import { ColSelectButtonContainer, DragHandlerWrapper } from './styles.tsx';
import { Position } from './type.ts';

const unfreezeIconContainerStyles = xcss({
  alignItems: 'center',
  display: 'inline-flex',
  height: '16px',
  justifyContent: 'center',
  position: 'relative',
  width: '16px'
});

const unfreezeIconSlashStyles = xcss({
  borderBlockStartColor: 'currentColor',
  borderBlockStartStyle: 'solid',
  borderBlockStartWidth: 'border.width',
  position: 'absolute',
  transform: 'rotate(45deg)',
  width: '18px'
});

const fieldTypeSubmenuHeadingStyles = xcss({
  paddingBlockStart: 'space.100',
  paddingBlockEnd: 'space.075',
  paddingInlineEnd: 'space.200',
  paddingInlineStart: 'space.200'
});

const UnfreezeIcon = () => (
  <Box as="span" aria-hidden="true" xcss={unfreezeIconContainerStyles}>
    <PinIcon label="" />
    <Box as="span" xcss={unfreezeIconSlashStyles} />
  </Box>
);

enum SELECT_FIELD_MENU_ACTIONS {
  ASK_ROVO = 'ask_rovo',
  EDIT_FIELD = 'edit_field',
  CHANGE_FIELD_TYPE = 'change_field_type',
  PIN_THROUGH_COLUMN = 'pin_through_column',
  UNPIN_COLUMN = 'unpin_column',
  ADD_FIELD_LEFT = 'add_field_left',
  ADD_FIELD_RIGHT = 'add_field_right',
  DUPLICATE_FIELDS = 'duplicate_fields',
  CLEAR_CELLS = 'clear_cells',
  DELETE_FIELDS = 'delete_fields',
  MOVE_FIELDS_LEFT = 'move_fields_left',
  MOVE_FIELDS_RIGHT = 'move_fields_right',
  FILTER_FIELD = 'filter_field',
  SORT_FIELD_INCREASING = 'sort_field_increasing',
  SORT_FIELD_DECREASING = 'sort_field_decreasing',
  HIDE_FIELDS = 'hide_fields'
}

export type PinnedLeadingFieldMenuConfig = {
  fieldIndex: number;
  pinnedLeadingFieldCount: number;
  maxPinnedLeadingFieldCount: number;
  onPinThroughThisColumn: () => void;
  onUnpinAllColumns: () => void;
  onFieldsInserted: ({
    insertBefore,
    selectedFieldIds
  }: {
    insertBefore: boolean;
    selectedFieldIds: ReadonlyArray<string>;
  }) => void;
  onFieldsRemoved: (fieldIds: ReadonlyArray<string>) => void;
  getViewChangesForFieldsHidden: (
    fieldIds: ReadonlyArray<string>
  ) => ReadonlyArray<ViewChangeType>;
};

type SharedSelectFieldMenuProps = {
  contentId: string;
  fieldId: string;
  currentViewId: string;
  viewSettings?: DatabaseViewSettings;
  canEditFields: boolean;
  onEditAiSummaryField?: () => void;
};

type HeaderSelectFieldMenuProps = SharedSelectFieldMenuProps & {
  surface?: 'table' | 'timeline';
  hoverProps?: HoverProps;
  handleSelectColumn: (e: React.MouseEvent | React.KeyboardEvent) => void;
  contextMenuAnchorPosition?: { x: number; y: number } | null;
  onOpenFromDragHandle?: () => void;
  onMoveFields?: (direction: 'left' | 'right') => void;
  isTriggerVisible?: boolean;
  hasUserEditedAiSummaryCells: () => boolean;
  onAiSummaryRerunEntireColumn?: () => void;
  onAiSummaryRerunEmptyEntries?: () => void;
  isAiSummaryRerunDisabled?: boolean;
  pinnedLeadingMenu?: PinnedLeadingFieldMenuConfig;
  renderEntryPanelTrigger?: never;
};

type EntryPanelSelectFieldMenuProps = SharedSelectFieldMenuProps & {
  surface: 'entry-panel';
  renderEntryPanelTrigger: (
    triggerProps: TriggerProps,
    controls: {
      isOpen: boolean;
      onClick: React.MouseEventHandler<HTMLButtonElement>;
    }
  ) => React.ReactElement | null;
  hoverProps?: never;
  handleSelectColumn?: never;
  contextMenuAnchorPosition?: never;
  onOpenFromDragHandle?: never;
  onMoveFields?: never;
  isTriggerVisible?: never;
  hasUserEditedAiSummaryCells?: never;
  onAiSummaryRerunEntireColumn?: never;
  onAiSummaryRerunEmptyEntries?: never;
  isAiSummaryRerunDisabled?: never;
  pinnedLeadingMenu?: never;
};

type SelectFieldMenuProps = HeaderSelectFieldMenuProps | EntryPanelSelectFieldMenuProps;

export type SelectFieldMenuHandle = {
  cancelPendingOpen: () => void;
  open: () => void;
  openAtPosition: (position: { x: number; y: number }) => Promise<void>;
};

export { FieldMenuSpotlightTarget, FieldMenuSpotlightTourProvider };

const EMPTY_SELECTED_FIELD_IDS: ReadonlyArray<string> = [];

const hasNoUserEditedAiSummaryCells = () => false;

const noopSelectColumn = () => undefined;

const getMenuAnchorPosition = (element: Element) => {
  const rect = element.getBoundingClientRect();

  return {
    x: rect.left,
    y: rect.bottom
  };
};

const getIsFieldSelected = (fieldId: string) =>
  getService('SelectionsReader').isFieldSelected(fieldId);

const RovoElemBefore = () => {
  useEffect(() => {
    getExperimentValue('databasesAIFC', 'enabled', {
      fireExperimentExposure: true
    });
  }, []);

  return <AskRovoIcon height={16} width={16} noPadding />;
};

const hasFieldBefore = (databaseFields: ReadonlyArray<{ id: string }>, fieldId: string) =>
  databaseFields.findIndex((field) => field.id === fieldId) > 0;

const hasFieldAfter = (
  databaseFields: ReadonlyArray<{ id: string }>,
  fieldId: string
) => {
  const fieldIndex = databaseFields.findIndex((field) => field.id === fieldId);

  return fieldIndex >= 0 && fieldIndex < databaseFields.length - 1;
};

const SelectFieldMenu = (
  {
    contentId,
    fieldId,
    currentViewId,
    viewSettings,
    hoverProps,
    handleSelectColumn,
    contextMenuAnchorPosition: externalContextMenuAnchorPosition,
    onOpenFromDragHandle,
    onMoveFields: onMoveTimelineFields,
    isTriggerVisible,
    surface = 'table',
    onEditAiSummaryField,
    hasUserEditedAiSummaryCells,
    onAiSummaryRerunEntireColumn,
    onAiSummaryRerunEmptyEntries,
    isAiSummaryRerunDisabled,
    canEditFields,
    pinnedLeadingMenu,
    renderEntryPanelTrigger
  }: SelectFieldMenuProps,
  ref: ForwardedRef<SelectFieldMenuHandle>
) => {
  const selectionsReader = getService('SelectionsReader');
  const actionBarPortal = useSnapshot(actionBarPortalState);
  const contextualMenu = useSnapshot(contextualMenuState);
  const isActionBarEnabled = actionBarPortal.isVisible;
  const isFieldContextualMenusEnabled = getExperimentValue(
    'databasesContextualMenusM2',
    'enabled',
    { fireExperimentExposure: false }
  );
  const isContextualMenusM2Enabled = isFieldContextualMenusEnabled;
  const isTimelineSurface = surface === 'timeline';
  const isEntryPanelSurface = surface === 'entry-panel';
  const usesApiFieldSelection = isTimelineSurface || isEntryPanelSurface;
  const resolvedHandleSelectColumn = handleSelectColumn ?? noopSelectColumn;
  const resolvedHasUserEditedAiSummaryCells =
    hasUserEditedAiSummaryCells ?? hasNoUserEditedAiSummaryCells;
  const menuData: MenuData = useMemo(
    () => ({
      type: 'field',
      fieldId,
      ...(isEntryPanelSurface ? { panel: true } : {})
    }),
    [fieldId, isEntryPanelSurface]
  );
  const { isMenuOpen, isPanel, openMenu, closeMenu, toggleOpenMenu } =
    useContextualMenu(menuData);
  const isEntryPanelMenuOpen = isEntryPanelSurface ? isMenuOpen && isPanel : isMenuOpen;
  const currentFieldMenuData =
    contextualMenu.menuData?.type === 'field' &&
    contextualMenu.menuData.fieldId === fieldId &&
    Boolean(contextualMenu.menuData.panel) === Boolean(menuData.panel)
      ? contextualMenu.menuData
      : null;
  const isCurrentFieldMenuOpen = currentFieldMenuData != null;
  const [isMenuSelectionActive, setIsMenuSelectionActive] = useState(false);
  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();
  const { database, databaseFields } = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId
    });

    return {
      database,
      databaseFields: getDatabaseFields(database)
    };
  });

  // Guard the heavier reads behind isSelected so unselected columns skip them.
  const isFieldSelectedForSurface = useCallback(
    () =>
      usesApiFieldSelection
        ? getService('SelectionsApi').isFieldSelected(fieldId)
        : selectionsReader.isFieldSelected(fieldId),
    [fieldId, selectionsReader, usesApiFieldSelection]
  );
  const isFieldSelected = isFieldSelectedForSurface();
  const shouldUseSingleFieldMenuSelection =
    isMenuSelectionActive || (isCurrentFieldMenuOpen && !isFieldSelected);
  const isSelected = isFieldSelected || isMenuSelectionActive || isCurrentFieldMenuOpen;
  const selectedFieldIds = !isSelected
    ? EMPTY_SELECTED_FIELD_IDS
    : shouldUseSingleFieldMenuSelection
      ? [fieldId]
      : usesApiFieldSelection
        ? getService('SelectionsApi').getSelectedFieldIds()
        : selectionsReader.getSelectedFieldIds();
  const selectedFieldCount = selectedFieldIds.length || 1;
  const isMultiFieldSelection = selectedFieldCount > 1;
  const isDeletionDisabled =
    isSelected &&
    (usesApiFieldSelection
      ? selectedFieldIds.length >= databaseFields.length
      : selectionsReader.isDeleteColumnsDisabled());
  const isSingleFieldMenu = !isFieldContextualMenusEnabled || !isMultiFieldSelection;
  const menuFieldCount = isFieldContextualMenusEnabled ? selectedFieldCount : 1;
  const selectedFieldIndexes = selectedFieldIds.flatMap((selectedFieldId) => {
    const index = databaseFields.findIndex((field) => field.id === selectedFieldId);

    return index === -1 ? [] : [index];
  });
  const firstSelectedFieldIndex = Math.min(...selectedFieldIndexes);
  const lastSelectedFieldIndex = Math.max(...selectedFieldIndexes);
  const hasFieldBeforeSelection =
    isSelected &&
    isSingleFieldMenu &&
    (shouldUseSingleFieldMenuSelection
      ? hasFieldBefore(databaseFields, fieldId)
      : usesApiFieldSelection
        ? firstSelectedFieldIndex > 0
        : selectionsReader.hasFieldBeforeSelection());
  const hasFieldAfterSelection =
    isSelected &&
    (shouldUseSingleFieldMenuSelection
      ? hasFieldAfter(databaseFields, fieldId)
      : usesApiFieldSelection
        ? lastSelectedFieldIndex < databaseFields.length - 1
        : selectionsReader.hasFieldAfterSelection());
  const canInsertFields =
    !isEntryPanelSurface && canEditFields && isSelected && isSingleFieldMenu;
  const canDuplicateFields = !isEntryPanelSurface && canEditFields && isSelected;
  const canDeleteFields = canEditFields && isSelected && !isDeletionDisabled;
  const canClearCells = !isEntryPanelSurface && canEditFields && isSelected;
  const canMoveRight =
    !isEntryPanelSurface && canEditFields && isSingleFieldMenu && hasFieldAfterSelection;
  const canMoveLeft = !isEntryPanelSurface && canEditFields && hasFieldBeforeSelection;
  const selectionIntent =
    isSelected && !usesApiFieldSelection ? selectionsReader.getSelectionIntent() : null;
  const isHoverDeleteFieldsButton =
    selectionIntent?.type === 'delete' && selectionIntent?.target === 'fields';

  const { formatMessage } = useIntl();
  const selectionsActions = getService('SelectionsActions');
  const { setViewChange, view } = useView();
  const client = useAnalytics();
  const { hidePanel, showPanel } = usePanel();
  const { getData } = usePanelState();
  const { fieldId: panelFieldId } = getData('FieldSettingsPanel') || {};

  const field = getDatabaseFieldRequired(database, fieldId);
  const aiSummaryRerunMenuItems = useAiSummaryRerunMenuItems({
    confirmationKey: `${contentId}:${fieldId}`,
    hasUserEditedCells: resolvedHasUserEditedAiSummaryCells,
    onRerunEntireColumn: onAiSummaryRerunEntireColumn,
    onRerunEmptyEntries: onAiSummaryRerunEmptyEntries,
    isDisabled: isAiSummaryRerunDisabled
  });
  const hasAiFieldHeaderRefresh = hasAiSummaryHeaderRefresh(field.type);
  const existingFieldNames = getExistingFieldNames(database, field);
  const isPinnedLeadingColumnsEnabled = getExperimentValue(
    'databasesPinnedLeadingColumns',
    'enabled',
    { fireExperimentExposure: false }
  );
  const isDBAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
  const isRovoAvailable = useIsDatabaseRovoAvailable();

  const [headerContextMenuAnchorPosition, setHeaderContextMenuAnchorPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const contextMenuAnchorPosition =
    externalContextMenuAnchorPosition ?? headerContextMenuAnchorPosition;
  const fieldMenuTriggerRef = useRef<HTMLElement | null>(null);
  const updateHeaderContextMenuAnchorPosition = useCallback(() => {
    if (externalContextMenuAnchorPosition) {
      return;
    }

    const fieldMenuTrigger = fieldMenuTriggerRef.current;
    if (!fieldMenuTrigger) {
      return;
    }

    const nextAnchorPosition = getMenuAnchorPosition(fieldMenuTrigger);
    setHeaderContextMenuAnchorPosition((previousAnchorPosition) =>
      previousAnchorPosition?.x === nextAnchorPosition.x &&
      previousAnchorPosition.y === nextAnchorPosition.y
        ? previousAnchorPosition
        : nextAnchorPosition
    );
  }, [externalContextMenuAnchorPosition]);

  const fieldMenuHeaderRef = useRef<FieldMenuHeaderHandle>(null);
  const fieldMenuHeaderSpotlightTargetRef = useRef<HTMLInputElement>(null);
  const freezeColumnSpotlightTargetRef = useRef<HTMLDivElement>(null);
  const prevIsSelected = useRef(isFieldSelectedForSurface());
  const isOpeningMenu = useRef(false);
  const openRequestIdRef = useRef(0);
  const wasEntryPanelMenuOpenRef = useRef(isEntryPanelMenuOpen);
  const deselectEntryPanelMenuField = useCallback(() => {
    if (!isEntryPanelSurface) {
      return;
    }

    wasEntryPanelMenuOpenRef.current = false;
    const selectionsApi = getService('SelectionsApi');
    if (selectionsApi.isFieldSelected(fieldId)) {
      selectionsApi.applyFieldSelection({ fieldId, gesture: 'toggle' });
    }
    selectionsActions.clearSelectionIntent();
  }, [fieldId, isEntryPanelSurface, selectionsActions]);

  useEffect(
    () => () => {
      const isEntryPanelFieldMenuOpen =
        isEntryPanelSurface && contextualMenuState.isMenuOpen(menuData);
      if (wasEntryPanelMenuOpenRef.current || isEntryPanelFieldMenuOpen) {
        deselectEntryPanelMenuField();
      }
      if (isEntryPanelFieldMenuOpen) {
        contextualMenuState.closeMenu();
      }
    },
    [deselectEntryPanelMenuField, isEntryPanelSurface, menuData]
  );
  const sendMenuOpenedAnalytics = useCallback(() => {
    manuallyFireExposureEvent('databasesContextualMenusM2');
    return client.sendFieldContextualMenuOpened({
      contentId,
      databaseContainer: getDatabaseContainerType(),
      ...getSelectionsAnalyticsAttributes(contentId)
    });
  }, [client, contentId]);
  const selectTableFieldForMenu = useCallback(
    ({ shouldClearSelectionIntent = true } = {}) => {
      if (!getIsFieldSelected(fieldId)) {
        selectionsActions.selectFields([fieldId]);
        setIsMenuSelectionActive(true);
      } else {
        setIsMenuSelectionActive(false);
      }

      if (shouldClearSelectionIntent) {
        selectionsActions.clearSelectionIntent();
      }
    },
    [fieldId, selectionsActions]
  );
  const beginTimelineMenuSelection = useCallback(() => {
    const selectionsApi = getService('SelectionsApi');
    const wasSelected = selectionsApi.isFieldSelected(fieldId);

    if (!wasSelected) {
      selectionsApi.applyFieldSelection({ fieldId, gesture: 'replace' });
    }

    setIsMenuSelectionActive(!wasSelected);
  }, [fieldId]);
  const handleEntryPanelTriggerClick = useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (isEntryPanelMenuOpen) {
        closeMenu();
        deselectEntryPanelMenuField();
        setIsMenuSelectionActive(false);
        return;
      }

      getService('SelectionsApi').applyFieldSelection({
        fieldId,
        gesture: 'replace'
      });
      setIsMenuSelectionActive(true);
      openMenu();
      void sendMenuOpenedAnalytics();
    },
    [
      closeMenu,
      deselectEntryPanelMenuField,
      fieldId,
      isEntryPanelMenuOpen,
      openMenu,
      sendMenuOpenedAnalytics
    ]
  );
  const resetTimelineMenuPresentationAfterClose = useCallback(() => {
    setHeaderContextMenuAnchorPosition(null);
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isTimelineSurface) {
        if (e.shiftKey || e.metaKey || e.ctrlKey) {
          if (isMenuOpen) {
            closeMenu();
          }

          contextualMenuState.closeMenu();
          isOpeningMenu.current = false;
          prevIsSelected.current = true;
          setHeaderContextMenuAnchorPosition(null);
          setIsMenuSelectionActive(false);
          resolvedHandleSelectColumn(e);
          return;
        }

        if (isMenuOpen) {
          isOpeningMenu.current = false;
          setHeaderContextMenuAnchorPosition(null);
          setIsMenuSelectionActive(false);
          closeMenu();
          return;
        }

        setHeaderContextMenuAnchorPosition(getMenuAnchorPosition(e.currentTarget));
        onOpenFromDragHandle?.();
        isOpeningMenu.current = true;

        if (!getIsFieldSelected(fieldId)) {
          prevIsSelected.current = true;
        } else {
          prevIsSelected.current = false;
        }
        selectTableFieldForMenu();

        afterNextLayout(async () => {
          try {
            await sendMenuOpenedAnalytics();
            selectTableFieldForMenu({ shouldClearSelectionIntent: false });
            openMenu();
          } finally {
            isOpeningMenu.current = false;
          }
        });
        return;
      }

      if (e.shiftKey || e.metaKey || e.ctrlKey) {
        openRequestIdRef.current += 1;
        if (isMenuOpen) {
          closeMenu();
        }

        contextualMenuState.closeMenu();
        isOpeningMenu.current = false;
        prevIsSelected.current = true;
        resetTimelineMenuPresentationAfterClose();
        resolvedHandleSelectColumn(e);
        return;
      }

      if (isMenuOpen) {
        openRequestIdRef.current += 1;
        isOpeningMenu.current = false;
        closeMenu();
        resetTimelineMenuPresentationAfterClose();
        return;
      }

      setHeaderContextMenuAnchorPosition(null);
      onOpenFromDragHandle?.();
      isOpeningMenu.current = true;
      openRequestIdRef.current += 1;

      if (!isFieldSelectedForSurface()) {
        prevIsSelected.current = true;
      } else {
        prevIsSelected.current = false;
      }
      beginTimelineMenuSelection();
      openMenu();
      void sendMenuOpenedAnalytics();
    },
    [
      beginTimelineMenuSelection,
      closeMenu,
      fieldId,
      resolvedHandleSelectColumn,
      isFieldSelectedForSurface,
      isMenuOpen,
      isTimelineSurface,
      openMenu,
      onOpenFromDragHandle,
      resetTimelineMenuPresentationAfterClose,
      selectTableFieldForMenu,
      sendMenuOpenedAnalytics
    ]
  );

  useImperativeHandle(
    ref,
    () => ({
      cancelPendingOpen() {
        if (!isTimelineSurface) {
          return;
        }

        openRequestIdRef.current += 1;
        isOpeningMenu.current = false;
        closeMenu();
        resetTimelineMenuPresentationAfterClose();
      },
      open() {
        setHeaderContextMenuAnchorPosition(null);
        if (isMenuOpen) {
          return;
        }
        openMenu();
        void sendMenuOpenedAnalytics();
      },
      openAtPosition(position) {
        setHeaderContextMenuAnchorPosition(position);

        if (!isTimelineSurface) {
          if (!getIsFieldSelected(fieldId)) {
            prevIsSelected.current = true;
          } else {
            prevIsSelected.current = false;
          }
          selectTableFieldForMenu();

          afterNextLayout(async () => {
            if (!isMenuOpen) {
              await sendMenuOpenedAnalytics();
            }

            selectTableFieldForMenu({ shouldClearSelectionIntent: false });
            openMenu();
            isOpeningMenu.current = false;
          });
          return Promise.resolve();
        }

        isOpeningMenu.current = true;
        openRequestIdRef.current += 1;

        if (!isFieldSelectedForSurface()) {
          prevIsSelected.current = true;
        } else {
          prevIsSelected.current = false;
        }
        beginTimelineMenuSelection();
        openMenu();
        isOpeningMenu.current = false;
        void sendMenuOpenedAnalytics();
        return Promise.resolve();
      }
    }),
    [
      beginTimelineMenuSelection,
      closeMenu,
      fieldId,
      isFieldSelectedForSurface,
      isMenuOpen,
      isTimelineSurface,
      openMenu,
      resetTimelineMenuPresentationAfterClose,
      selectTableFieldForMenu,
      sendMenuOpenedAnalytics
    ]
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (!isTimelineSurface) {
        if (
          getIsFieldSelected(fieldId) &&
          !prevIsSelected.current &&
          !isMenuOpen &&
          !isOpeningMenu.current
        ) {
          resolvedHandleSelectColumn(e);
        }
        return;
      }

      if (isOpeningMenu.current) {
        isOpeningMenu.current = false;
        return;
      }

      if (isFieldSelectedForSurface() && !prevIsSelected.current && !isMenuOpen) {
        // remove selection if the user clicks on the select button again
        resolvedHandleSelectColumn(e);
      }
    },
    [
      fieldId,
      resolvedHandleSelectColumn,
      isFieldSelectedForSurface,
      isMenuOpen,
      isTimelineSurface
    ]
  );

  const handleRightClick = (e: React.MouseEvent) => e.preventDefault();
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key !== 'Enter' && e.key !== ' ') {
        return;
      }

      e.preventDefault();

      if (!isTimelineSurface) {
        setHeaderContextMenuAnchorPosition(getMenuAnchorPosition(e.currentTarget));
        onOpenFromDragHandle?.();

        if (!isMenuOpen) {
          await sendMenuOpenedAnalytics();
        }

        toggleOpenMenu();
        selectTableFieldForMenu();
        return;
      }

      openRequestIdRef.current += 1;

      if (isMenuOpen) {
        isOpeningMenu.current = false;
        toggleOpenMenu();
        resetTimelineMenuPresentationAfterClose();
        return;
      }

      setHeaderContextMenuAnchorPosition(null);
      onOpenFromDragHandle?.();
      beginTimelineMenuSelection();
      openMenu();
      void sendMenuOpenedAnalytics();
    },
    [
      beginTimelineMenuSelection,
      isMenuOpen,
      isTimelineSurface,
      openMenu,
      onOpenFromDragHandle,
      resetTimelineMenuPresentationAfterClose,
      selectTableFieldForMenu,
      sendMenuOpenedAnalytics,
      toggleOpenMenu
    ]
  );

  const handleSaveFieldName = useCallback(
    (name: string) => {
      dispatch(
        addChangeToDraft(
          contentId,
          {
            type: DatabaseChangeType.SET_FIELD_NAME,
            fieldId,
            name,
            delta: getTextDelta(field.name, name, field.name.length)
          },
          {
            fieldSettingsAnalytics: {
              source: 'fieldContextualMenu',
              fieldType: field.type
            }
          }
        )
      );
    },
    [contentId, dispatch, field.name, field.type, fieldId]
  );

  const handleSaveFieldDescription = useCallback(
    (description: string) => {
      const currentDescription = field.description ?? '';
      dispatch(
        addChangeToDraft(
          contentId,
          {
            type: DatabaseChangeType.SET_FIELD_DESCRIPTION,
            fieldId,
            description,
            delta: getTextDelta(
              currentDescription,
              description,
              currentDescription.length
            )
          },
          {
            fieldSettingsAnalytics: {
              source: 'fieldContextualMenu',
              fieldType: field.type
            }
          }
        )
      );
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_SHOW_FIELD_DESCRIPTION,
          fieldId,
          showDescription: description.length > 0
        })
      );
    },
    [contentId, dispatch, field.description, field.type, fieldId]
  );

  const handleMenuClose = useCallback(
    (event?: Event | React.MouseEvent | React.KeyboardEvent | null) => {
      const isEscapeEvent = event != null && 'key' in event && event.key === 'Escape';
      if (isContextualMenusM2Enabled && !isEscapeEvent) {
        fieldMenuHeaderRef.current?.save();
      }

      if (isTimelineSurface) {
        openRequestIdRef.current += 1;
        resetTimelineMenuPresentationAfterClose();
      } else {
        deselectEntryPanelMenuField();
        setHeaderContextMenuAnchorPosition(null);
        setIsMenuSelectionActive(false);
      }
      isOpeningMenu.current = false;
    },
    [
      isContextualMenusM2Enabled,
      isTimelineSurface,
      deselectEntryPanelMenuField,
      resetTimelineMenuPresentationAfterClose
    ]
  );

  useEffect(() => {
    if (!isEntryPanelSurface) {
      return;
    }

    const didMenuClose = wasEntryPanelMenuOpenRef.current && !isEntryPanelMenuOpen;
    wasEntryPanelMenuOpenRef.current = isEntryPanelMenuOpen;

    if (didMenuClose) {
      deselectEntryPanelMenuField();
    }

    if (!isEntryPanelMenuOpen) {
      setIsMenuSelectionActive(false);
    }
  }, [deselectEntryPanelMenuField, isEntryPanelMenuOpen, isEntryPanelSurface]);

  useEffect(() => {
    if (!headerContextMenuAnchorPosition || externalContextMenuAnchorPosition) {
      return;
    }

    let animationFrame: number | null = null;
    const updateAnchorPosition = () => {
      if (animationFrame != null) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        animationFrame = null;
        updateHeaderContextMenuAnchorPosition();
      });
    };

    window.addEventListener('resize', updateAnchorPosition);
    window.addEventListener('scroll', updateAnchorPosition, true);

    return () => {
      if (animationFrame != null) {
        cancelAnimationFrame(animationFrame);
      }
      window.removeEventListener('resize', updateAnchorPosition);
      window.removeEventListener('scroll', updateAnchorPosition, true);
    };
  }, [
    externalContextMenuAnchorPosition,
    headerContextMenuAnchorPosition,
    updateHeaderContextMenuAnchorPosition
  ]);

  const Trigger = useComponent(
    (popupTriggerProps: TriggerProps) => {
      if (isEntryPanelSurface && renderEntryPanelTrigger) {
        return renderEntryPanelTrigger(popupTriggerProps, {
          isOpen: isEntryPanelMenuOpen,
          onClick: handleEntryPanelTriggerClick
        });
      }

      const { ref, ...triggerProps } = popupTriggerProps;
      const isTriggerActive =
        isMenuOpen && (isTimelineSurface ? isFieldSelectedForSurface() : isSelected);
      const shouldShowTrigger = Boolean(isTriggerVisible || isTriggerActive);
      const handleFieldMenuTriggerRef = (element: HTMLElement | null) => {
        fieldMenuTriggerRef.current = element;

        if (!contextMenuAnchorPosition) {
          if (typeof ref === 'function') {
            ref(element);
          } else if (ref) {
            (ref as React.MutableRefObject<HTMLElement | null>).current = element;
          }
        }
      };

      return (
        <ColSelectButtonContainer className={IGNORED_TABLE_ELEMENT_CLASS} {...hoverProps}>
          {contextMenuAnchorPosition && (
            <span
              ref={ref}
              style={{
                position: 'fixed',
                // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
                left: contextMenuAnchorPosition.x,
                // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
                top: contextMenuAnchorPosition.y,
                width: 1,
                height: 1,
                pointerEvents: 'none'
              }}
            />
          )}
          <DragHandlerWrapper
            ref={handleFieldMenuTriggerRef}
            type="button"
            className={TABLE_CLASSNAMES.SELECT_BUTTON}
            $fieldPresentation={
              isTimelineSurface ? { isVisible: shouldShowTrigger } : undefined
            }
            isActive={isTriggerActive}
            isDeletionActive={isHoverDeleteFieldsButton}
            data-interactive
            data-field-select-button
            data-testid="field-select-button"
            {...triggerProps}
            aria-label={formatMessage(messages['table-layout.select-column-label'])}
            aria-expanded={isMenuOpen}
            aria-haspopup="menu"
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onClick={handleClick}
            onContextMenu={handleRightClick}
            onKeyDown={handleKeyDown}
            style={
              isTimelineSurface
                ? undefined
                : {
                    backgroundColor:
                      isMenuOpen && isSelected
                        ? token('color.background.accent.blue.subtler.pressed')
                        : token('color.background.accent.gray.subtlest.hovered')
                  }
            }
          >
            <div className={TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON}>
              <ActiveHorizontalDragHandlerIcon
                label={TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON}
              />
            </div>
            <div className={TABLE_CLASSNAMES.DRAG_HANDLER_ICON}>
              <HorizontalDragHandlerIcon label={TABLE_CLASSNAMES.DRAG_HANDLER_ICON} />
            </div>
          </DragHandlerWrapper>
        </ColSelectButtonContainer>
      );
    },
    [
      hoverProps,
      contextMenuAnchorPosition,
      isMenuOpen,
      isHoverDeleteFieldsButton,
      isEntryPanelMenuOpen,
      isSelected,
      formatMessage,
      handleMouseDown,
      handleMouseUp,
      handleClick,
      handleEntryPanelTriggerClick,
      handleKeyDown,
      isEntryPanelSurface,
      isFieldSelectedForSurface,
      isTimelineSurface,
      isTriggerVisible,
      renderEntryPanelTrigger
    ]
  );

  const handleInsertFields = useCallback(
    async (position: Position) => {
      const selectedFieldIds = selectionsReader.getFieldIdsFromSelection();
      const newFieldLabel = formatMessage(
        messages['field-settings.field-name-inline-edit-default-text'],
        {
          defaultFieldName: formatMessage(getFieldTypes()[DatabaseFieldType.TEXT].label)
        }
      );
      traceDatabaseAction(
        {
          action:
            position === Position.BEFORE
              ? DatabaseTraceAction.FieldInsertBefore
              : DatabaseTraceAction.FieldInsertAfter,
          source: DatabaseTraceSource.FieldMenu
        },
        () => {
          dispatch(
            doInsertFields(
              contentId,
              currentViewId,
              position === Position.BEFORE,
              newFieldLabel
            )
          );
          pinnedLeadingMenu?.onFieldsInserted({
            insertBefore: position === Position.BEFORE,
            selectedFieldIds
          });
        }
      );
      await client.sendInsertRowEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
        source: 'databaseView'
      });
    },
    [
      client,
      contentId,
      currentViewId,
      dispatch,
      formatMessage,
      pinnedLeadingMenu,
      selectionsReader
    ]
  );

  const handleDuplicateFields = useCallback(() => {
    const selectedFieldIds = isPinnedLeadingColumnsEnabled
      ? selectionsReader.getFieldIdsFromSelection()
      : [];
    traceDatabaseAction(
      {
        action: DatabaseTraceAction.FieldDuplicate,
        source: DatabaseTraceSource.FieldMenu
      },
      () => {
        dispatch(doDuplicateEntireFields(contentId, view.id));
        if (isPinnedLeadingColumnsEnabled) {
          pinnedLeadingMenu?.onFieldsInserted({
            insertBefore: false,
            selectedFieldIds
          });
        }
      }
    );
  }, [
    contentId,
    dispatch,
    isPinnedLeadingColumnsEnabled,
    pinnedLeadingMenu,
    selectionsReader,
    view.id
  ]);

  const handleClearFields = useCallback(() => {
    traceDatabaseAction(
      { action: DatabaseTraceAction.FieldClear, source: DatabaseTraceSource.FieldMenu },
      () => {
        dispatch(doClearEntireFields(contentId));
      }
    );
  }, [contentId, dispatch]);

  const { showDialog } = useDialog();

  const onLeaveDeleteFieldButton = useCallback(() => {
    selectionsActions.clearSelectionIntent();
  }, [selectionsActions]);

  const handleDeleteFields = useCallback(() => {
    const fieldNames: string[] = [];
    const database = selectDatabaseByContentIdRequired(store.getState(), {
      contentId
    });
    const selectedEntireFieldIds = isEntryPanelSurface
      ? [fieldId]
      : getService('SelectionsReader').getSelectedFieldIds();
    selectedEntireFieldIds.forEach((fieldId) => {
      if (database.fieldsById[fieldId]) {
        fieldNames.push(database.fieldsById[fieldId].name);
      }
    });
    showDialog('ConfirmDialog', {
      heading: formatMessage(messages['field-settings.delete-field']),
      body: (
        <Stack space="space.150">
          <Box>
            {formatMessage(
              messages[
                'field-settings.delete-multiple-fields-confirmation-message.fields'
              ],
              {
                fieldsNumber: fieldNames.length,
                fields: <Text weight="bold">{fieldNames.join(', ')}</Text>
              }
            )}
          </Box>
          <Box>
            {formatMessage(
              messages[
                'field-settings.delete-multiple-fields-confirmation-message.additional'
              ]
            )}
          </Box>
        </Stack>
      ),
      yesLabel: formatMessage(messages['delete-buttons.delete-field-button']),
      noLabel: formatMessage(messages['delete-buttons.delete-field-cancel-button']),
      appearance: 'danger',
      onClose: (result: boolean) => {
        onLeaveDeleteFieldButton();
        if (result) {
          if (getService('SelectionsReader').isFieldInSelection(panelFieldId)) {
            hidePanel();
          }

          if (fieldHasCalculations(fieldId, viewSettings?.calculations)) {
            client.sendRemoveCalculationEvent({
              databaseContainer: getDatabaseContainerType(),
              fieldId,
              fieldType: database.fieldsById[fieldId].type,
              source: 'selectFieldMenu',
              contentId: database.contentId
            });
          }

          traceDatabaseAction(
            {
              action: DatabaseTraceAction.FieldDelete,
              source: DatabaseTraceSource.FieldMenu
            },
            () => {
              dispatch(
                doDeleteFieldsWithIds(
                  contentId,
                  selectedEntireFieldIds,
                  isPinnedLeadingColumnsEnabled && pinnedLeadingMenu
                    ? () => pinnedLeadingMenu.onFieldsRemoved(selectedEntireFieldIds)
                    : undefined
                )
              );

              selectionsActions.deselectAll();
            }
          );

          selectedEntireFieldIds.forEach((id) => {
            const deletedField = database.fieldsById[id];
            if (deletedField) {
              void client.sendDeleteFieldEvent({
                contentId,
                databaseContainer: getDatabaseContainerType(),
                fieldIdDeleted: deletedField.id,
                fieldName: deletedField.name,
                fieldType: deletedField.type
              });
            }
          });
        }
      }
    });
  }, [
    store,
    contentId,
    showDialog,
    formatMessage,
    onLeaveDeleteFieldButton,
    panelFieldId,
    fieldId,
    viewSettings?.calculations,
    dispatch,
    client,
    hidePanel,
    isEntryPanelSurface,
    isPinnedLeadingColumnsEnabled,
    pinnedLeadingMenu,
    selectionsActions
  ]);

  const handleMoveFields = useCallback(
    (position: Position) => {
      if (isTimelineSurface && onMoveTimelineFields) {
        onMoveTimelineFields(position === Position.BEFORE ? 'left' : 'right');
        return;
      }

      traceDatabaseAction(
        {
          action:
            position === Position.BEFORE
              ? DatabaseTraceAction.FieldMoveLeft
              : DatabaseTraceAction.FieldMoveRight,
          source: DatabaseTraceSource.FieldMenu
        },
        () => {
          dispatch(doMoveFields(contentId, viewSettings, position === Position.BEFORE));
        }
      );
      afterNextLayout(() => selectionsActions.selectFields([fieldId]));
    },
    [
      contentId,
      dispatch,
      fieldId,
      isTimelineSurface,
      onMoveTimelineFields,
      selectionsActions,
      viewSettings
    ]
  );

  const handleSortChange = useCallback(
    (sort: DatabaseViewSettings['sorts'][number]) => {
      const matchingSort = view.settings.sorts.find((s) => s.fieldId === sort.fieldId);
      if (matchingSort) {
        setViewChange({
          type: ViewChangeTypes.VIEW_SORT_UPDATE_VALUE,
          fieldId: sort.fieldId,
          ascending: sort.ascending,
          customOrder: sort.customOrder
        });
      } else {
        setViewChange({
          type: ViewChangeTypes.VIEW_SORT_APPEND,
          sort
        });
      }
      void sendSortEvent(
        client,
        sort,
        SendDatabaseSortEventSources.FIELD_MENU,
        field ? field.type : undefined,
        contentId
      );
    },
    [client, contentId, field, setViewChange, view.settings.sorts]
  );

  const filters = view.settings.filter as
    | {
        type: DatabaseFilterUsageType;
        value: DatabaseFilter[];
      }
    | undefined;

  const fieldFilters = useMemo(
    () =>
      filters?.value.filter((filter) => 'fieldId' in filter && filter.fieldId === fieldId)
        .length || 0,
    [filters, fieldId]
  );
  const selectedSort = useMemo(
    () => view.settings.sorts.find((sort) => sort.fieldId === fieldId),
    [fieldId, view.settings.sorts]
  );
  const selectedSortDirection = selectedSort
    ? getSelectedSortDirection(selectedSort)
    : undefined;
  const isSortAppliedToField = selectedSort !== undefined;

  const { setActivePanelSetting } = useViewUiState();
  const isViewContextualMenusEnabled = getExperimentValue(
    'databasesViewContextualMenus',
    'enabled',
    { fireExperimentExposure: false }
  );
  const isAiFieldsSummaryEnabled = useIsAiSummaryFieldEnabled();
  const shouldShowFieldMenuHeader =
    isFieldContextualMenusEnabled ||
    (field.type === DatabaseFieldType.AI_SUMMARY && isAiFieldsSummaryEnabled);
  const handleFilterField = useCallback(() => {
    manuallyFireExposureEvent('databasesViewContextualMenus');

    if (isViewContextualMenusEnabled) {
      contextualMenuState.showMenu({
        type: 'view-filter',
        viewId: view.id,
        fieldIdForFilterToCreate: fieldId,
        sourceForFilterToCreate: 'databaseFieldActions'
      });
    } else {
      setActivePanelSetting(ViewPanelSettingType.FILTER);
      showPanel('ViewSettingsPanel', {
        canEditViews: canEditFields,
        fieldIdForFilterToCreate: fieldId
      });
    }
  }, [
    fieldId,
    isViewContextualMenusEnabled,
    setActivePanelSetting,
    showPanel,
    canEditFields,
    view.id
  ]);

  const handleHideFields = useCallback(() => {
    const database = selectDatabaseByContentIdRequired(store.getState(), {
      contentId
    });
    const fields = getDatabaseFields(database);
    const selectedFieldIdsToHide = isMenuSelectionActive
      ? [fieldId]
      : selectionsReader.getSelectedFieldIds();
    const fieldIdsToHide =
      isFieldContextualMenusEnabled && selectedFieldIdsToHide.length > 0
        ? selectedFieldIdsToHide
        : [fieldId];
    const viewChanges: ViewChangeType[] = fieldIdsToHide.map((fieldIdToHide) => ({
      type: ViewChangeTypes.VIEW_FIELDS,
      fieldKey: fieldIdToHide,
      fieldValue: false,
      fields
    }));
    const pinnedLeadingViewChanges =
      pinnedLeadingMenu?.getViewChangesForFieldsHidden(fieldIdsToHide) ?? [];
    setViewChange(
      pinnedLeadingViewChanges.length > 0
        ? [...viewChanges, ...pinnedLeadingViewChanges]
        : viewChanges
    );
    selectionsActions.deselectAll();
    const field = getDatabaseFieldRequired(database, fieldId);
    void client.sendHideColumnsEvent({
      source: 'databaseView',
      contentId: contentId,
      databaseContainer: getDatabaseContainerType(),
      hidden: true,
      fieldType: field.type
    });
  }, [
    client,
    contentId,
    fieldId,
    isFieldContextualMenusEnabled,
    isMenuSelectionActive,
    pinnedLeadingMenu,
    selectionsActions,
    selectionsReader,
    setViewChange,
    store
  ]);

  const addFieldLeftMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.ADD_FIELD_LEFT,
      content: isFieldContextualMenusEnabled
        ? formatMessage(messages['field-actions.insert-submenu-left'])
        : formatMessage(messages['field-actions.add-left']),
      onClick: () => handleInsertFields(Position.BEFORE),
      shortcut: SHORTCUTS.ADD_FIELD_LEFT,
      elemBefore: <InsertColumnBeforeIcon label="" />
    }),
    [formatMessage, handleInsertFields, isFieldContextualMenusEnabled]
  );

  const addFieldRightMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.ADD_FIELD_RIGHT,
      content: isFieldContextualMenusEnabled
        ? formatMessage(messages['field-actions.insert-submenu-right'])
        : formatMessage(messages['field-actions.add-right']),
      onClick: () => handleInsertFields(Position.AFTER),
      shortcut: SHORTCUTS.ADD_FIELD_RIGHT,
      elemBefore: <InsertColumnAfterIcon label="" />
    }),
    [formatMessage, handleInsertFields, isFieldContextualMenusEnabled]
  );

  const duplicateFieldsMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.DUPLICATE_FIELDS,
      content:
        isFieldContextualMenusEnabled && selectedFieldCount === 1
          ? formatMessage(messages['field-actions.duplicate-single'])
          : formatMessage(messages['field-actions.duplicate'], {
              fieldCount: menuFieldCount
            }),
      onClick: handleDuplicateFields,
      shortcut: SHORTCUTS.DUPLICATE,
      elemBefore: <CopyIcon label="" />
    }),
    [
      formatMessage,
      handleDuplicateFields,
      isFieldContextualMenusEnabled,
      menuFieldCount,
      selectedFieldCount
    ]
  );

  const clearCellsMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.CLEAR_CELLS,
      content: formatMessage(messages['value-actions.clear-cells'], { valuesCount: 2 }),
      onClick: handleClearFields,
      shortcut: SHORTCUTS.CLEAR,
      elemBefore: isFieldContextualMenusEnabled ? (
        <TableCellClearIcon label="" />
      ) : (
        <CrossCircleIcon label="" />
      )
    }),
    [formatMessage, handleClearFields, isFieldContextualMenusEnabled]
  );

  const onHoverDeleteFieldButton = useCallback(() => {
    selectionsActions.setSelectionIntent({ type: 'delete', target: 'fields' });
  }, [selectionsActions]);

  const deleteFieldsMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.DELETE_FIELDS,
      content:
        isFieldContextualMenusEnabled && selectedFieldCount === 1
          ? formatMessage(messages['field-actions.delete-single'])
          : formatMessage(messages['field-actions.delete'], {
              fieldCount: menuFieldCount
            }),
      onClick: handleDeleteFields,
      onMouseEnter: onHoverDeleteFieldButton,
      onMouseLeave: onLeaveDeleteFieldButton,
      shortcut: SHORTCUTS.DELETE,
      elemBefore: (
        <DeleteIcon
          label=""
          color={
            isHoverDeleteFieldsButton ? token('color.icon.danger', '#C9372C') : undefined
          }
        />
      )
    }),
    [
      formatMessage,
      handleDeleteFields,
      isHoverDeleteFieldsButton,
      isFieldContextualMenusEnabled,
      menuFieldCount,
      onHoverDeleteFieldButton,
      onLeaveDeleteFieldButton,
      selectedFieldCount
    ]
  );

  const moveFieldsLeftMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.MOVE_FIELDS_LEFT,
      content: formatMessage(messages['field-actions.move-left'], {
        fieldCount: 1
      }),
      onClick: () => handleMoveFields(Position.BEFORE),
      shortcut: SHORTCUTS.MOVE_FIELDS_LEFT,
      elemBefore: <ArrowLeftIcon label="" />
    }),
    [formatMessage, handleMoveFields]
  );

  const moveFieldsRightMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.MOVE_FIELDS_RIGHT,
      content: formatMessage(messages['field-actions.move-right'], {
        fieldCount: 1
      }),
      onClick: () => handleMoveFields(Position.AFTER),
      shortcut: SHORTCUTS.MOVE_FIELDS_RIGHT,
      elemBefore: <ArrowRightIcon label="" />
    }),
    [formatMessage, handleMoveFields]
  );

  const filterFieldItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.FILTER_FIELD,
      content: formatMessage(
        messages[
          isFieldContextualMenusEnabled
            ? 'field-actions.filter-short'
            : 'field-actions.filter'
        ]
      ),
      onClick: handleFilterField,
      elemAfter:
        fieldFilters > 0 ? (
          <Badge
            style={{
              backgroundColor: token(
                'color.background.accent.blue.subtlest.hovered',
                '#CCE0FF'
              )
            }}
            testId="filters-badge"
          >
            {fieldFilters}
          </Badge>
        ) : undefined,
      elemBefore: <FilterIcon label="" />
    }),
    [fieldFilters, formatMessage, handleFilterField, isFieldContextualMenusEnabled]
  );

  const sortFieldIncreasingItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.SORT_FIELD_INCREASING,
      content: formatMessage(messages['field-actions.sort-increasing']),
      onClick: () =>
        handleSortChange({
          fieldId,
          ascending: true
        }),
      elemBefore: <SortDescendingIcon label="" />
    }),
    [fieldId, formatMessage, handleSortChange]
  );

  const sortFieldDecreasingItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.SORT_FIELD_DECREASING,
      content: formatMessage(messages['field-actions.sort-decreasing']),
      onClick: () =>
        handleSortChange({
          fieldId,
          ascending: false
        }),
      elemBefore: <SortAscendingIcon label="" />
    }),
    [fieldId, formatMessage, handleSortChange]
  );

  const sortFieldSubmenuItems: MenuItem[] = useMemo(
    () =>
      createFieldSortSubmenuItems({
        field,
        fieldId,
        formatMessage,
        selectedDirection: selectedSortDirection,
        onSortChange: handleSortChange
      }),
    [field, fieldId, formatMessage, handleSortChange, selectedSortDirection]
  );

  const sortFieldItem: MenuItem = useMemo(
    () => ({
      key: 'sort-field',
      content: formatMessage(messages['field-settings-picker.sort-picker-button-text']),
      submenuItems: sortFieldSubmenuItems,
      submenuWidth: MENU_WIDTH.SUBMENU,
      isActive: isSortAppliedToField,
      elemBefore: <SortIcon label="" />
    }),
    [formatMessage, isSortAppliedToField, sortFieldSubmenuItems]
  );

  const hideFieldsItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.HIDE_FIELDS,
      content: formatMessage(
        messages[
          isFieldContextualMenusEnabled && selectedFieldCount === 1
            ? 'field-actions.hide-single'
            : 'field-actions.hide-fields'
        ],
        {
          fieldCount: menuFieldCount
        }
      ),
      onClick: handleHideFields,
      value: { name: SELECT_FIELD_MENU_ACTIONS.HIDE_FIELDS },
      elemBefore: <HideIcon label="" />
    }),
    [
      formatMessage,
      handleHideFields,
      isFieldContextualMenusEnabled,
      menuFieldCount,
      selectedFieldCount
    ]
  );

  const insertFieldMenuItem: MenuItem = useMemo(
    () => ({
      key: 'insert-field',
      content: formatMessage(messages['table-layout.insert-column-tooltip']),
      submenuItems: [addFieldRightMenuItem, addFieldLeftMenuItem],
      submenuWidth: MENU_WIDTH.PRIMARY,
      elemBefore: <InsertColumnAfterIcon label="" />
    }),
    [formatMessage, addFieldLeftMenuItem, addFieldRightMenuItem]
  );

  const handlePinnedLeadingMenuAction = useCallback(
    ({
      action,
      nextFrozenColumnCount,
      onAction
    }: {
      action: 'pinned' | 'unpinned';
      nextFrozenColumnCount: number;
      onAction: () => void;
    }) => {
      if (!pinnedLeadingMenu) {
        return;
      }

      const previousFrozenColumnCount = pinnedLeadingMenu.pinnedLeadingFieldCount;
      const reliability = ReliabilityMetrics.getInstance();

      try {
        onAction();
        reliability.success(MetricName.DATABASE_UPDATE_FROZEN_COLUMNS, { action });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        reliability.failure(MetricName.DATABASE_UPDATE_FROZEN_COLUMNS, {
          action,
          errorMessage
        });
        throw error;
      }

      void client.sendFrozenColumnsUpdatedEvent({
        action,
        contentId,
        databaseContainer: getDatabaseContainerType(),
        source: 'fieldContextualMenu',
        previousFrozenColumnCount,
        nextFrozenColumnCount,
        targetFieldIndex: pinnedLeadingMenu.fieldIndex,
        maxFrozenColumnCount: pinnedLeadingMenu.maxPinnedLeadingFieldCount,
        ...getSelectionsAnalyticsAttributes(contentId)
      });
      selectionsActions.deselectAll();
    },
    [client, contentId, pinnedLeadingMenu, selectionsActions]
  );

  const pinThroughColumnMenuItem: MenuItem | null = useMemo(() => {
    if (!isPinnedLeadingColumnsEnabled || !pinnedLeadingMenu) {
      return null;
    }
    const wouldExceedMax =
      pinnedLeadingMenu.fieldIndex + 1 > pinnedLeadingMenu.maxPinnedLeadingFieldCount;
    const isFieldFrozen =
      pinnedLeadingMenu.fieldIndex < pinnedLeadingMenu.pinnedLeadingFieldCount;

    if (isFieldFrozen) {
      return null;
    }

    return {
      key: SELECT_FIELD_MENU_ACTIONS.PIN_THROUGH_COLUMN,
      content: (
        <FieldMenuSpotlightTarget
          alignToContextualMenu
          step="freeze-column"
          placement="right-end"
          offset={[0, 24]}
          targetRef={freezeColumnSpotlightTargetRef}
        >
          <Box ref={freezeColumnSpotlightTargetRef}>
            {formatMessage(messages['field-actions.pin-through-column'])}
          </Box>
        </FieldMenuSpotlightTarget>
      ),
      closeOnClick: !wouldExceedMax,
      isDisabled: wouldExceedMax,
      tooltipDescription: wouldExceedMax
        ? formatMessage(messages['field-actions.pin-through-column-disabled-at-limit'])
        : undefined,
      tooltipPosition: 'right',
      onClick: () =>
        handlePinnedLeadingMenuAction({
          action: 'pinned',
          nextFrozenColumnCount: pinnedLeadingMenu.fieldIndex + 1,
          onAction: pinnedLeadingMenu.onPinThroughThisColumn
        }),
      elemBefore: <PinIcon label="" />
    };
  }, [
    formatMessage,
    handlePinnedLeadingMenuAction,
    isPinnedLeadingColumnsEnabled,
    pinnedLeadingMenu
  ]);

  const unpinAllColumnsMenuItem: MenuItem | null = useMemo(() => {
    if (!isPinnedLeadingColumnsEnabled || !pinnedLeadingMenu) {
      return null;
    }
    if (pinnedLeadingMenu.fieldIndex >= pinnedLeadingMenu.pinnedLeadingFieldCount) {
      return null;
    }
    return {
      key: SELECT_FIELD_MENU_ACTIONS.UNPIN_COLUMN,
      content: (
        <FieldMenuSpotlightTarget
          alignToContextualMenu
          step="freeze-column"
          placement="right-end"
          offset={[0, 24]}
          targetRef={freezeColumnSpotlightTargetRef}
        >
          <Box ref={freezeColumnSpotlightTargetRef}>
            {formatMessage(messages['field-actions.unpin-column'])}
          </Box>
        </FieldMenuSpotlightTarget>
      ),
      closeOnClick: true,
      onClick: () =>
        handlePinnedLeadingMenuAction({
          action: 'unpinned',
          nextFrozenColumnCount: 0,
          onAction: pinnedLeadingMenu.onUnpinAllColumns
        }),
      elemBefore: <UnfreezeIcon />
    };
  }, [
    formatMessage,
    handlePinnedLeadingMenuAction,
    isPinnedLeadingColumnsEnabled,
    pinnedLeadingMenu
  ]);

  const askRovoFieldMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.ASK_ROVO,
      name: 'ask-rovo',
      content: formatMessage(messages['value-actions.ask-rovo']),
      onClick: () => openInlineRovoPrompt(InlineRovoPromptTriggerSource.TableField),
      elemBefore: <RovoElemBefore />
    }),
    [formatMessage]
  );

  const editFieldMenuItem: MenuItem = useMemo(() => {
    const title = formatMessage(messages['table-layout.field-settings.label']);

    if (field.type === DatabaseFieldType.AI_SUMMARY) {
      return {
        key: SELECT_FIELD_MENU_ACTIONS.EDIT_FIELD,
        content: title,
        onClick: onEditAiSummaryField,
        elemBefore: <EditIcon label="" />
      };
    }

    return {
      key: SELECT_FIELD_MENU_ACTIONS.EDIT_FIELD,
      content: {
        type: 'drilldown-menu',
        title,
        width: MENU_WIDTH.DRILLDOWN,
        content: () => (
          <FieldSettingsMenuContent contentId={contentId} fieldId={fieldId} />
        )
      },
      onClick: () => {
        void client.sendDatabaseFieldSettingsOpenedEvent({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          source: 'fieldContextualMenu',
          fieldType: field.type
        });
      },
      elemBefore: <EditIcon label="" />
    };
  }, [client, contentId, field.type, fieldId, formatMessage, onEditAiSummaryField]);

  const rootPage =
    currentFieldMenuData?.initialPage === 'edit-field-settings' &&
    isDrilldownMenu(editFieldMenuItem.content)
      ? editFieldMenuItem.content
      : undefined;

  const { basic, confluence, jira } = useFieldTypeEntries(undefined, 'contextual-menu');
  const { field: draftField, changeFieldType } = useChangeFieldType({
    contentId,
    fieldId,
    source: 'fieldContextualMenu'
  });
  const fieldTypeSubmenuItems: MenuItem[] = useMemo(() => {
    const submenuItems: MenuItem[] = [];
    const addSection = (key: string, label: string, fieldTypes: typeof basic) => {
      if (fieldTypes.length === 0) {
        return;
      }

      submenuItems.push({
        key: `${key}_heading`,
        content: (
          <Box xcss={fieldTypeSubmenuHeadingStyles}>
            <Text size="small" weight="semibold" color="color.text.subtle">
              {label}
            </Text>
          </Box>
        )
      });
      for (const fieldType of fieldTypes) {
        const Icon =
          fieldType.type === DatabaseFieldType.JIRA_ISSUE_DETAILS
            ? ArrowsDiagonalUpRightDownLeftIcon
            : fieldType.Icon;
        const isSelected = draftField.type === fieldType.type;
        const iconColor = isSelected ? token('color.icon.selected') : undefined;
        const content =
          fieldType.type === DatabaseFieldType.HYPERLINK
            ? formatMessage(messages['field-settings.change-field-type.hyperlink'])
            : fieldType.type === DatabaseFieldType.ATTACHMENT
              ? formatMessage(messages['field-settings.change-field-type.attachment'])
              : fieldType.label;
        submenuItems.push({
          key: `change_field_type_${fieldType.type}`,
          content,
          elemBefore: Icon ? (
            <Icon label="" color={iconColor} />
          ) : (
            <FieldTypeIcon
              type={fieldType.type}
              label={fieldType.labelDescriptor}
              noTooltip
              color={iconColor}
            />
          ),
          isActive: isSelected,
          onClick: () => changeFieldType(fieldType.type)
        });
      }
    };

    addSection(
      'change_field_type_basic',
      formatMessage(messages['field-settings.change-field-type.basic-fields']),
      basic
    );
    addSection(
      'change_field_type_confluence',
      formatMessage(
        messages['field-settings.change-field-type.advanced-confluence-fields']
      ),
      confluence
    );
    addSection(
      'change_field_type_jira',
      formatMessage(messages['field-settings.change-field-type.advanced-jira-fields']),
      jira
    );

    return submenuItems;
  }, [basic, changeFieldType, confluence, draftField.type, formatMessage, jira]);

  const changeFieldTypeMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.CHANGE_FIELD_TYPE,
      content: formatMessage(messages['field-settings.change-field-type']),
      elemBefore: <ChangesIcon label="" />,
      submenuItems: fieldTypeSubmenuItems,
      submenuWidth: MENU_WIDTH.PRIMARY,
      submenuMaxHeight: '328px',
      submenuScrollGradient: true
    }),
    [fieldTypeSubmenuItems, formatMessage]
  );

  const quickCalculationItem = useQuickCalcMenuItem({
    field,
    contentId,
    canEditFields
  });

  const menuItems = useMemo(() => {
    if (isEntryPanelSurface) {
      const shouldShowEntryPanelEditField =
        canEditFields &&
        (field.type !== DatabaseFieldType.AI_SUMMARY ||
          (isAiFieldsSummaryEnabled && Boolean(onEditAiSummaryField)));
      const entryPanelItems = [
        ...(shouldShowEntryPanelEditField ? [editFieldMenuItem] : []),
        ...(canDeleteFields ? [deleteFieldsMenuItem] : [])
      ];

      return entryPanelItems.length > 0 ? [{ items: entryPanelItems }] : [];
    }

    const firstGroup: MenuItem[] = [];
    const isSingleEditableFieldMenu =
      !isFieldContextualMenusEnabled || (canEditFields && !isMultiFieldSelection);
    const rerunGroup: MenuItem[] =
      isSingleEditableFieldMenu && isAiFieldsSummaryEnabled && hasAiFieldHeaderRefresh
        ? aiSummaryRerunMenuItems
        : [];
    const secondGroup: MenuItem[] = [];
    const thirdGroup: MenuItem[] = [];
    const fourthGroup: MenuItem[] = [];

    if (
      isDBAiFirstCreationEnabled &&
      isRovoAvailable &&
      fieldTypeSupportsAskRovo(field.type) &&
      getExperimentValue('databasesAIFC', 'enabled', {
        fireExperimentExposure: false
      })
    ) {
      firstGroup.push(askRovoFieldMenuItem);
    }
    const shouldShowEditField =
      field.type === DatabaseFieldType.AI_SUMMARY
        ? isAiFieldsSummaryEnabled && Boolean(onEditAiSummaryField)
        : isFieldContextualMenusEnabled;

    if (shouldShowEditField && isSingleEditableFieldMenu) {
      firstGroup.push(editFieldMenuItem);
      firstGroup.push(changeFieldTypeMenuItem);
    }

    if (isFieldContextualMenusEnabled && !canEditFields && !isMultiFieldSelection) {
      secondGroup.push(filterFieldItem);
      secondGroup.push(sortFieldItem);
    } else if (isFieldContextualMenusEnabled && isMultiFieldSelection) {
      secondGroup.push(hideFieldsItem);
      if (canDuplicateFields) {
        secondGroup.push(duplicateFieldsMenuItem);
      }
      if (canClearCells) {
        thirdGroup.push(clearCellsMenuItem);
      }
      if (canDeleteFields) {
        thirdGroup.push(deleteFieldsMenuItem);
      }
    } else if (isFieldContextualMenusEnabled) {
      secondGroup.push(filterFieldItem);
      secondGroup.push(sortFieldItem);
      secondGroup.push(hideFieldsItem);
      if (quickCalculationItem) {
        secondGroup.push(quickCalculationItem);
      }
      if (unpinAllColumnsMenuItem) {
        secondGroup.push(unpinAllColumnsMenuItem);
      }
      if (pinThroughColumnMenuItem) {
        secondGroup.push(pinThroughColumnMenuItem);
      }
      if (canInsertFields) {
        thirdGroup.push(insertFieldMenuItem);
      }
      if (canDuplicateFields) {
        thirdGroup.push(duplicateFieldsMenuItem);
      }
      if (canClearCells) {
        fourthGroup.push(clearCellsMenuItem);
      }
      if (canDeleteFields) {
        fourthGroup.push(deleteFieldsMenuItem);
      }
    } else {
      if (canInsertFields) {
        secondGroup.push(addFieldRightMenuItem);
        secondGroup.push(addFieldLeftMenuItem);
      }
      if (canDuplicateFields) {
        secondGroup.push(duplicateFieldsMenuItem);
      }
      if (canClearCells) {
        secondGroup.push(clearCellsMenuItem);
      }
      if (canDeleteFields) {
        secondGroup.push(deleteFieldsMenuItem);
      }
      if (canMoveLeft) {
        thirdGroup.push(moveFieldsLeftMenuItem);
      }
      if (canMoveRight) {
        thirdGroup.push(moveFieldsRightMenuItem);
      }
      if (unpinAllColumnsMenuItem) {
        fourthGroup.push(unpinAllColumnsMenuItem);
      }
      if (pinThroughColumnMenuItem) {
        fourthGroup.push(pinThroughColumnMenuItem);
      }
      if (quickCalculationItem) {
        fourthGroup.push(quickCalculationItem);
      }
      fourthGroup.push(filterFieldItem);
      fourthGroup.push(sortFieldIncreasingItem);
      fourthGroup.push(sortFieldDecreasingItem);
      fourthGroup.push(hideFieldsItem);
    }
    return [
      { items: firstGroup },
      { items: rerunGroup },
      { items: secondGroup },
      { items: thirdGroup },
      { items: fourthGroup }
    ];
  }, [
    canInsertFields,
    canDuplicateFields,
    canClearCells,
    canDeleteFields,
    canMoveLeft,
    canMoveRight,
    aiSummaryRerunMenuItems,
    askRovoFieldMenuItem,
    editFieldMenuItem,
    changeFieldTypeMenuItem,
    addFieldRightMenuItem,
    addFieldLeftMenuItem,
    duplicateFieldsMenuItem,
    clearCellsMenuItem,
    deleteFieldsMenuItem,
    moveFieldsLeftMenuItem,
    moveFieldsRightMenuItem,
    quickCalculationItem,
    filterFieldItem,
    sortFieldIncreasingItem,
    sortFieldDecreasingItem,
    sortFieldItem,
    hideFieldsItem,
    hasAiFieldHeaderRefresh,
    field.type,
    canEditFields,
    isAiFieldsSummaryEnabled,
    isFieldContextualMenusEnabled,
    isMultiFieldSelection,
    isDBAiFirstCreationEnabled,
    isRovoAvailable,
    onEditAiSummaryField,
    pinThroughColumnMenuItem,
    unpinAllColumnsMenuItem,
    insertFieldMenuItem,
    isEntryPanelSurface
  ]);

  const isShortcutMenuOpen = isEntryPanelSurface ? isEntryPanelMenuOpen : isMenuOpen;

  useKeyboardShortcut(
    isShortcutMenuOpen && canInsertFields
      ? SHORTCUTS.ADD_FIELD_LEFT.keyboardShortCut
      : undefined,
    () => handleInsertFields(Position.BEFORE)
  );
  useKeyboardShortcut(
    isShortcutMenuOpen && canInsertFields
      ? SHORTCUTS.ADD_FIELD_RIGHT.keyboardShortCut
      : undefined,
    () => handleInsertFields(Position.AFTER)
  );
  useKeyboardShortcut(
    isShortcutMenuOpen && canDuplicateFields
      ? SHORTCUTS.DUPLICATE.keyboardShortCut
      : undefined,
    handleDuplicateFields
  );
  // The selections engine owns Backspace for all modes to avoid duplicate
  // dispatches and broken undo.
  useKeyboardShortcut(undefined, handleClearFields);
  useKeyboardShortcut(
    isShortcutMenuOpen && canDeleteFields ? SHORTCUTS.DELETE.keyboardShortCut : undefined,
    handleDeleteFields
  );
  useKeyboardShortcut(
    isShortcutMenuOpen && canMoveRight
      ? SHORTCUTS.MOVE_FIELDS_RIGHT.keyboardShortCut
      : undefined,
    () => handleMoveFields(Position.AFTER)
  );
  useKeyboardShortcut(
    isShortcutMenuOpen && canMoveLeft
      ? SHORTCUTS.MOVE_FIELDS_LEFT.keyboardShortCut
      : undefined,
    () => () => handleMoveFields(Position.BEFORE)
  );

  return (
    <ContextualMenu
      menuData={menuData}
      items={menuItems}
      trigger={Trigger}
      header={
        !isEntryPanelSurface && shouldShowFieldMenuHeader && isSingleFieldMenu ? (
          <FieldMenuSpotlightTarget
            alignToContextualMenu
            step="field-rename"
            placement="right-end"
            offset={[0, 16]}
            targetRef={fieldMenuHeaderSpotlightTargetRef}
            waitForTargetLayout
          >
            <FieldMenuHeader
              ref={fieldMenuHeaderRef}
              name={field.name}
              description={field.description}
              existingFieldNames={existingFieldNames}
              onSaveName={handleSaveFieldName}
              onSaveDescription={handleSaveFieldDescription}
              onRequestClose={closeMenu}
              nameInputRef={fieldMenuHeaderSpotlightTargetRef}
            />
          </FieldMenuSpotlightTarget>
        ) : undefined
      }
      onClose={handleMenuClose}
      panel={isEntryPanelSurface}
      placement="bottom-start"
      isActionBarEnabled={
        !isEntryPanelSurface && isActionBarEnabled && !contextMenuAnchorPosition
      }
      rootPage={rootPage}
      width={MENU_WIDTH.PRIMARY}
    />
  );
};

export default React.memo(React.forwardRef(SelectFieldMenu));
